import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { CheckCircle2, Loader2 } from "lucide-react";

const COPY = {
  demo: {
    title: "Request a demo",
    desc: "See your own risk posture come alive. Tell us a little about you and we'll be in touch to arrange a walkthrough.",
    cta: "Request demo",
    done: "Thank you — your demo request is in. We'll reach out shortly to arrange a walkthrough.",
  },
  brochure: {
    title: "Request the brochure",
    desc: "We'll send the ERIO platform brochure straight to your inbox.",
    cta: "Send me the brochure",
    done: "Thank you — we've received your request and will send the brochure to your inbox shortly.",
  },
};

export function LeadFormDialog({ kind = "demo", source = "", trigger }) {
  const copy = COPY[kind] || COPY.demo;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", message: "", company_website: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const { i18n } = useTranslation();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm({ name: "", email: "", company: "", role: "", message: "", company_website: "" });
    setError("");
    setDone(false);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/leads", { ...form, kind, source });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTimeout(reset, 200);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`lead-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle data-testid="lead-dialog-title">{copy.title}</DialogTitle>
          <DialogDescription>{copy.desc}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="lead-success">
            <CheckCircle2 className="text-green-600" size={40} />
            <p className="text-sm text-ek-ink/70">{copy.done}</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="lead-close-button"
              className="ek-btn mt-2"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="lead-form">
            {/* Honeypot — hidden from humans, traps bots */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.company_website}
              onChange={set("company_website")}
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
              aria-hidden="true"
            />
            <div>
              <label className="mb-1 block text-xs font-medium text-ek-ink">Full name *</label>
              <input
                value={form.name}
                onChange={set("name")}
                required
                data-testid="lead-name-input"
                placeholder="Jane Smith"
                className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ek-ink">Work email *</label>
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                required
                data-testid="lead-email-input"
                placeholder="jane@company.com"
                className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-ek-ink">Company *</label>
                <input
                  value={form.company}
                  onChange={set("company")}
                  required
                  data-testid="lead-company-input"
                  placeholder="Acme Ltd"
                  className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-ek-ink">Role</label>
                <input
                  value={form.role}
                  onChange={set("role")}
                  data-testid="lead-role-input"
                  placeholder="CRO"
                  className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
                />
              </div>
            </div>
            {kind === "demo" && (
              <div>
                <label className="mb-1 block text-xs font-medium text-ek-ink">What would you like to see?</label>
                <textarea
                  value={form.message}
                  onChange={set("message")}
                  rows={3}
                  data-testid="lead-message-input"
                  placeholder="Tell us about your risk programme or what you'd like the demo to cover."
                  className="w-full resize-none rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
                />
              </div>
            )}

            {error && (
              <p className="text-sm text-red-600" data-testid="lead-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="lead-submit-button"
              className="ek-btn w-full disabled:opacity-60"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" /> Sending…
                </span>
              ) : (
                copy.cta
              )}
            </button>
            <p className="text-center text-[0.65rem] text-ek-ink/65">
              We&apos;ll only use your details to respond to this request.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
